import React, { FunctionComponent, useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '../../../Component/Icon/Icon';
import { ShoppingPlannerContext } from '../../../Context/ShoppingPlannerContext';
import { Variant, VariantSelector } from '../VariantSelector/VariantSelector';
import { InstallationAndCare } from '../../../Api/AccessoriesApi';
import { SmallButton } from '../../../Component/SmallButton/SmallButton';
import { useResponsive } from '../../../Hook/UseResponsive';
import { AccessoriesPage } from '../AccessoriesPage';
import { useProductsFromStates } from '../../../Hook/UseProductsFromStates';
import { urls } from '../../../Constants/Urls';

import './InstallationPage.scss';
import '../../../Component/SelectionMarker/SelectionMarker.scss';

export const InstallationPage: FunctionComponent = () => {
    const { accessories, selectedInstallationAndCare, setSelectedInstallationAndCare } =
        useContext(ShoppingPlannerContext);

    const { isLargerThanMd } = useResponsive();
    const { floor } = useProductsFromStates();

    const [variants, setVariants] = useState<Variant[]>([]);

    const filteredAccessories = accessories.installationAndCare.filter(
        (accessory) =>
            floor &&
            (accessory.suitableFloorFamilies.length === 0 ||
                accessory.suitableFloorFamilies.includes(floor.floorFamilyId))
    );

    const closeAction = () => {
        setVariants([]);
    };

    const selectAction = (articleNumber: string) => {
        setSelectedInstallationAndCare((currentSelectedInstallationAndCare) => {
            if (selectedInstallationAndCare.includes(articleNumber)) {
                return currentSelectedInstallationAndCare.filter(
                    (articleNumberFromProduct) => articleNumberFromProduct !== articleNumber
                );
            } else {
                return [...currentSelectedInstallationAndCare, articleNumber];
            }
        });
    };

    const variantSelected = (variant: Variant) => selectedInstallationAndCare.includes(variant.articleNumber);

    const selected = (currentInstallationAndCare: InstallationAndCare) =>
        currentInstallationAndCare.variants.some((variant) =>
            selectedInstallationAndCare.includes(variant.articleNumber)
        );

    return (
        <>
            <AccessoriesPage className="shopping-planner-installation-page">
                {isLargerThanMd ? (
                    <aside className="shopping-planner-sidebar">
                        <h3 className="shopping-planner-sidebar__headline">Wählen Sie hier das Verlege Zubehör aus.</h3>
                        <p>
                            Unsere Produkte entsprechen den besonderen Anforderungen von Laminatböden. Zu den einzelnen
                            Zubehör-Artikeln erhalten Sie automatisch Mengenempfehlungen.
                        </p>
                    </aside>
                ) : null}

                <div className="shopping-planner__scroll-area">
                    <div className="shopping-planner-accessories-page__accessories">
                        {isLargerThanMd ? null : (
                            <h2 className="shopping-planner-accessories-page__headline">Verlegung & Pflege</h2>
                        )}

                        <div className="shopping-planner-accessories-page__breadcrumbs">
                            <Link
                                to={urls.steps.accessoriesInsulation.builder()}
                                className="shopping-planner-accessories-page__headline"
                            >
                                Dämmung
                            </Link>
                            <Icon icon="arrow" />
                            <Link
                                to={urls.steps.accessoriesSkirting.builder()}
                                className="shopping-planner-accessories-page__headline"
                            >
                                Leisten
                            </Link>
                            <Icon icon="arrow" />
                            <h2 className="shopping-planner-accessories-page__headline">Verlegung & Pflege</h2>
                        </div>
                        <ul className="shopping-planner-installations">
                            {filteredAccessories.map((installation) => (
                                <li
                                    key={installation.accessoryId}
                                    className={[
                                        'shopping-planner-installation',
                                        selected(installation) ? 'shopping-planner-installation--selected' : '',
                                    ].join(' ')}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        selectAction(
                                            installation.variants.find((variant) => variantSelected(variant))
                                                ?.articleNumber ?? installation.variants[0].articleNumber
                                        );
                                    }}
                                >
                                    <img src={installation.imageUri ?? ''}></img>
                                    <div className="shopping-planner-installation__description">
                                        <div className="shopping-planner-installation__label">{installation.label}</div>
                                        {installation.variants.length > 1 ? (
                                            <SmallButton
                                                className="shopping-planner-accessories-page__detail-button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setVariants(installation.variants);
                                                }}
                                            >
                                                Details
                                            </SmallButton>
                                        ) : null}
                                    </div>

                                    {selected(installation) ? (
                                        <Icon icon="check" className="shopping-planner-selection-marker" />
                                    ) : null}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </AccessoriesPage>
            {variants.length > 1 ? (
                <VariantSelector
                    variants={variants}
                    selectAction={selectAction}
                    closeAction={closeAction}
                    isSelected={variantSelected}
                />
            ) : null}
        </>
    );
};
